/**
 * JWT Token Utility Functions
 * Handles token validation, expiration checking, and parsing
 */

export interface JWTPayload {
  exp: number;
  iat: number;
  sub?: string;
  email?: string;
  roles?: string[];
  [key: string]: any;
}

/**
 * Parse JWT token without external dependencies
 * @param token - JWT token string
 * @returns Parsed payload or null if invalid
 */
export const parseJWT = (token: string): JWTPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @returns true if expired, false if valid
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = parseJWT(token);
    if (!payload || !payload.exp) return true;
    
    // Add 30 second buffer to prevent edge cases
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < (currentTime + 30);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Check if a JWT token will expire soon (within the next 5 minutes)
 * @param token - JWT token string
 * @returns true if expiring soon, false otherwise
 */
export const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const payload = parseJWT(token);
    if (!payload || !payload.exp) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const fiveMinutes = 5 * 60; // 5 minutes in seconds
    
    return payload.exp < (currentTime + fiveMinutes);
  } catch (error) {
    console.error('Error checking if token expires soon:', error);
    return true;
  }
};

/**
 * Get time until token expires in seconds
 * @param token - JWT token string
 * @returns seconds until expiration, negative if expired
 */
export const getTimeUntilExpiration = (token: string): number => {
  try {
    const payload = parseJWT(token);
    if (!payload || !payload.exp) return -1;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp - currentTime;
  } catch (error) {
    console.error('Error getting time until expiration:', error);
    return -1;
  }
};

/**
 * Get token payload information
 * @param token - JWT token string
 * @returns Token information object
 */
export const getTokenInfo = (token: string) => {
  try {
    const payload = parseJWT(token);
    if (!payload) return null;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp < currentTime;
    const timeUntilExpiry = payload.exp - currentTime;
    
    return {
      payload,
      isExpired,
      timeUntilExpiry,
      expiresAt: new Date(payload.exp * 1000),
      issuedAt: new Date(payload.iat * 1000),
      subject: payload.sub,
      email: payload.email,
      roles: payload.roles || []
    };
  } catch (error) {
    console.error('Error getting token info:', error);
    return null;
  }
}; 